import { useState } from "react";
import { useMemo } from "react";
import { TestimonialColumnContainer } from "./testimonial-column-container";
import { Heading } from "../ui/heading";
import { Subheading } from "../ui/subheading";
import { cn } from "@nextui-org/react";
import { InViewDiv } from "../ui/in-view-div";
import { Container } from "../ui/container";
import { MessagesSquare } from "lucide-react";
import { FeatureIconContainer } from "../features/feature-icon-container";
import { testimonials } from "../../constants/testimonials";

export const Testimonials = () => {
  return (
    <div className="relative py-10 px-4 md:py-40 bg-black">
      <div className="hidden md:block" data-aos="fade-down">
        <FeatureIconContainer className="flex justify-center items-center overflow-hidden">
          <MessagesSquare className="h-6 w-6 text-fuchsia-700" />
        </FeatureIconContainer>
        <Heading className="mt-4">Loved by Store Owners Worldwide</Heading>
        <Subheading className="text-center max-w-lg mx-auto">
          Whizmanage is trusted by thousands of WooCommerce users. Our tools
          simplify store management and enhance productivity, earning us a
          dedicated user base.
        </Subheading>
        <TestimonialGrid />
      </div>
      <div className="md:hidden">
        <FeatureIconContainer className="flex justify-center items-center overflow-hidden">
          <MessagesSquare className="h-6 w-6 text-fuchsia-700" />
        </FeatureIconContainer>
        <Heading className="mt-4">Loved by Store Owners Worldwide</Heading>
        <Subheading className="text-center max-w-lg mx-auto">
          Whizmanage is trusted by thousands of WooCommerce users. Our tools
          simplify store management and enhance productivity, earning us a
          dedicated user base.
        </Subheading>
        <TestimonialGrid />
      </div>
    </div>
  );
};

function Testimonial({ name, quote, src, designation, className, ...props }) {
  let animationDelay = useMemo(() => {
    let possibleAnimationDelays = [
      "0s",
      "0.1s",
      "0.2s",
      "0.3s",
      "0.4s",
      "0.5s",
    ];
    return possibleAnimationDelays[
      Math.floor(Math.random() * possibleAnimationDelays.length)
    ];
  }, []);

  const boxStyle = {};
  return (
    <figure
      className={cn(
        "animate-fade-in rounded-3xl p-8 opacity-0 shadow-derek bg-neutral-900 m-8",
        className
      )}
      style={{
        animationDelay,
      }}
      {...props}
    >
      <div className="flex flex-col items-start">
        <div className="flex gap-2">
          <img
            src={src}
            width={150}
            height={150}
            className="h-10 w-10 !rounded-full"
            alt={name}
          />
          <div>
            <h3 className="text-sm  font-medium text-neutral-300">{name}</h3>
            <p className="text-sm font-normal text-neutral-300">
              {designation}
            </p>
          </div>
        </div>
        <p className="text-base mt-4 text-neutral-400">{quote}</p>
      </div>
    </figure>
  );
}

function TestimonialColumn({
  testimonials,
  className,
  containerClassName,
  shift = 0,
}) {
  return (
    <TestimonialColumnContainer className={cn(className)} shift={shift}>
      {testimonials
        .concat(testimonials)
        .map((testimonial, testimonialIndex) => (
          <Testimonial
            name={testimonial.name}
            quote={testimonial.quote}
            src={testimonial.src}
            designation={testimonial.designation}
            key={testimonialIndex}
            className={containerClassName?.(
              testimonialIndex % testimonials.length
            )}
          />
        ))}
    </TestimonialColumnContainer>
  );
}

function splitArray(array, numParts) {
  let result = [];
  for (let i = 0; i < array.length; i++) {
    let index = i % numParts;
    if (!result[index]) {
      result[index] = [];
    }
    result[index].push(array[i]);
  }
  return result;
}

function TestimonialGrid() {
  let columns = splitArray(testimonials, 3);
  let column1 = columns[0];
  let column2 = columns[1];
  let column3 = splitArray(columns[2], 2);
  return (
    <div className="relative container mx-auto mt-16 grid h-[49rem] max-h-[150vh] grid-cols-1 items-start gap-8 overflow-hidden p-0 md:px-4 xl:px-32 sm:mt-20 md:grid-cols-2 lg:grid-cols-3">
      <TestimonialColumn
        className="!gap-8"
        testimonials={[...column1, ...column3.flat(), ...column2]}
        containerClassName={(tIndex) =>
          cn(
            tIndex >= column1.length + column3[0].length && "md:hidden",
            tIndex >= column1.length && "lg:hidden"
          )
        }
        shift={10}
      />
      <TestimonialColumn
        testimonials={[...column2, ...column3[1]]}
        className="hidden md:flex"
        containerClassName={(tIndex) =>
          tIndex >= column2.length ? "lg:hidden" : ""
        }
        shift={15}
      />
      <TestimonialColumn
        testimonials={column3.flat()}
        className="hidden lg:flex"
        shift={10}
      />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black" />
    </div>
  );
}
